/**
 * Midlife Trial LP buybox — plan card sync, pricing, scroll helpers.
 */

(function () {
  if (window.__ctrMlTrialLpBuyboxInitialized) return;
  window.__ctrMlTrialLpBuyboxInitialized = true;

  const BUYBOX_ID = "ctr-midlife-trial-lp-product-buybox";
  const HEADER_OFFSET = 65;
  const SCROLL_SELECTOR = "[data-ctr-ml-trial-lp-scroll-to-buybox]";
  const SCROLL_TO_TESTIMONIALS_SELECTOR = "[data-ctr-ml-trial-lp-scroll-to-testimonials]";
  const DEFAULT_TESTIMONIALS_SECTION_ID = "ctr-midlife-trial-lp-testimonials";
  const ATC_SELECTOR = "[data-ctr-ml-trial-lp-trigger-atc]";
  const ONE_TIME_ATC_SELECTOR = "[data-ctr-ml-trial-lp-trigger-one-time-atc]";
  const PLAN_CARD_SELECTOR = "[data-ctr-ml-trial-lp-plan-card]";

  function getAllBuyboxRoots() {
    return Array.from(document.querySelectorAll("[data-ctr-ml-trial-lp-buybox]"));
  }

  function getBuyboxElement() {
    return (
      document.getElementById(BUYBOX_ID) ||
      getAllBuyboxRoots()[0]?.closest("section") ||
      document.querySelector(".ctr-midlife-trial-lp-product-buybox")
    );
  }

  function getBuyboxRoot() {
    return getAllBuyboxRoots()[0] || null;
  }

  function shouldRedirectToCheckout(root) {
    root = root || getBuyboxRoot();
    return root?.dataset?.ctrMlTrialLpRedirectCheckout === "true";
  }

  function buildAddToCartOptions(root) {
    const options = {
      niceTitle: root?.dataset?.niceTitle || "",
    };

    if (shouldRedirectToCheckout(root)) {
      options.redirectToCheckout = true;
    }

    return options;
  }

  function getCheckoutUrl() {
    return window.Shopify?.routes?.root
      ? `${window.Shopify.routes.root}checkout`
      : "/checkout";
  }

  async function performBuyboxAddToCart(formData, root) {
    const options = buildAddToCartOptions(root);
    const redirect = Boolean(options.redirectToCheckout);
    const offCanvasStore = redirect ? Alpine.store("offCanvasCart") : null;
    const originalOpenOffcanvas = offCanvasStore?.openOffcanvas?.bind(offCanvasStore);

    if (redirect && offCanvasStore && originalOpenOffcanvas) {
      offCanvasStore.openOffcanvas = () => {};
    }

    try {
      await formData.addToCart(options);

      if (redirect) {
        window.location.href = getCheckoutUrl();
      }
    } finally {
      if (redirect && offCanvasStore && originalOpenOffcanvas) {
        offCanvasStore.openOffcanvas = originalOpenOffcanvas;
      }
    }
  }

  let variantSyncLock = false;
  let frequencySyncLock = false;

  function getFrequencySellingPlanId(input) {
    if (!input) return null;
    try {
      return Alpine.$data(input)?.sellingPlanID ?? null;
    } catch (_e) {
      return null;
    }
  }

  function getFrequencyPlanLabel(input) {
    if (!input) return "";
    try {
      return Alpine.$data(input)?.frequencyLabel || input.value || "";
    } catch (_e) {
      return input.value || "";
    }
  }

  function findPlanCardByLabel(root, planLabel) {
    if (!root || !planLabel) return null;
    const normalized = planLabel.trim().toLowerCase();
    return (
      Array.from(root.querySelectorAll(PLAN_CARD_SELECTOR)).find((card) => {
        const label =
          card.dataset.planLabel ||
          card.querySelector(".ctr-midlife-trial-lp-product-buybox__plan-card-label")?.textContent ||
          "";
        return label.trim().toLowerCase() === normalized;
      }) || null
    );
  }

  function findFrequencyInputInRoot(root, { sellingPlanId, planLabel } = {}) {
    if (!root) return null;

    const inputs = root.querySelectorAll(
      '.ctr-midlife-trial-lp-product-buybox__skio-engine [name="frequency"]'
    );
    const normalizedLabel = planLabel?.trim().toLowerCase();

    for (const input of inputs) {
      const inputPlanId = getFrequencySellingPlanId(input);
      const inputLabel = getFrequencyPlanLabel(input).trim().toLowerCase();

      if (sellingPlanId != null && inputPlanId == sellingPlanId) {
        return input;
      }

      if (normalizedLabel && inputLabel === normalizedLabel) {
        return input;
      }
    }

    return null;
  }

  function getSelectedVariantFromRoot(root) {
    const formData = getSkioFormData(root);
    if (!formData?.currentPacks?.length) return null;
    return formData.currentPacks[formData.currentPacks.length - 1];
  }

  function findVariantSelectorData(root, variantId) {
    if (!root || !variantId) return null;

    for (const selectorEl of root.querySelectorAll(".variant-pack-form__selector")) {
      try {
        const data = Alpine.$data(selectorEl);
        if (data?.variant?.id === variantId) return data;
      } catch (_e) {
        /* Alpine may not be ready */
      }
    }

    return null;
  }

  function getBuyboxGalleryWrap(root) {
    return (
      root?.querySelector(".ctr-midlife-trial-lp-product-buybox__gallery-wrap.mfr-product__top") ||
      root?.querySelector(".ctr-midlife-trial-lp-product-buybox__gallery-wrap") ||
      null
    );
  }

  function getBuyboxSelectorsData(root) {
    const selectorsEl = root?.querySelector(".variant-pack-form__selectors");
    if (!selectorsEl) return null;

    try {
      return Alpine.$data(selectorsEl);
    } catch (_e) {
      return null;
    }
  }

  function hasFixedGallery(root) {
    return root?.dataset?.ctrMlTrialLpFixedGallery === "true";
  }

  function bindBuyboxGallerySection(root) {
    const galleryWrap = getBuyboxGalleryWrap(root);
    const selectorsData = getBuyboxSelectorsData(root);
    if (!galleryWrap || !selectorsData) return null;

    selectorsData.$section = galleryWrap;

    if (hasFixedGallery(root) && !selectorsData.__ctrMlTrialLpFixedGalleryBound) {
      selectorsData.onVariantChange = function (newVariantId, variantMeta = {}) {
        const {
          productTitle = "",
          productVendor = "",
          productType = "",
          variantPrice = 0,
          variantId = newVariantId,
          variantTitle = "",
          variantImage = false,
        } = variantMeta || {};

        this.variantData = {
          productTitle,
          productVendor,
          productType,
          variantPrice,
          variantId,
          variantTitle,
        };

        if (variantImage && variantImage.length) {
          this.itemImage = variantImage;
        }
      };
      selectorsData.__ctrMlTrialLpFixedGalleryBound = true;
    }

    return selectorsData;
  }

  function syncVariantGallery(root, variantId, variantMeta) {
    if (hasFixedGallery(root)) return;

    const galleryWrap = getBuyboxGalleryWrap(root);
    const mediaEl = galleryWrap?.querySelector(".product-top__media");
    if (!mediaEl || !variantId) return;

    const selectorsData = bindBuyboxGallerySection(root);
    if (!selectorsData?.onVariantChange) return;

    if (selectorsData.activeCarousel == variantId) return;

    const payload =
      variantMeta || findVariantSelectorData(root, variantId)?.variantChangePayload || { variantId };

    selectorsData.onVariantChange(variantId, payload);
  }

  function applyVariantPackToRoot(root, variantObj) {
    const formData = getSkioFormData(root);
    if (!formData || !variantObj?.id) return false;

    const alreadySelected = formData.isInPack?.(variantObj.id) && formData.isFull?.();
    if (alreadySelected) return false;

    if (formData.isSingleSelect?.()) {
      formData.currentPacks = [{ ...variantObj }];
    } else {
      formData.reset?.();
      formData.addToPack?.(variantObj);
    }

    return true;
  }

  function syncVariantPackAcrossBuyboxes(sourceRoot, variantObj) {
    if (variantSyncLock || !variantObj?.id) return;

    variantSyncLock = true;
    try {
      getAllBuyboxRoots().forEach((root) => {
        if (root === sourceRoot) return;
        applyVariantPackToRoot(root, variantObj);
      });
    } finally {
      variantSyncLock = false;
    }
  }

  function resyncVariantPackAfterFrequencyChange(sourceRoot) {
    window.setTimeout(() => {
      const variantObj = getSelectedVariantFromRoot(sourceRoot);
      if (variantObj) {
        syncVariantPackAcrossBuyboxes(sourceRoot, variantObj);
        return;
      }

      initBuyboxVariantPreselect(sourceRoot);
      const syncedVariant = getSelectedVariantFromRoot(sourceRoot);
      if (syncedVariant) {
        syncVariantPackAcrossBuyboxes(sourceRoot, syncedVariant);
      }
    }, 50);
  }

  function syncVariantAcrossBuyboxes(sourceRoot, variantObj, variantMeta) {
    if (variantSyncLock || !variantObj?.id) return;

    variantSyncLock = true;
    try {
      const payload =
        variantMeta || findVariantSelectorData(sourceRoot, variantObj.id)?.variantChangePayload;

      getAllBuyboxRoots().forEach((root) => {
        bindBuyboxGallerySection(root);

        if (root === sourceRoot) {
          syncVariantGallery(root, variantObj.id, payload);
          return;
        }

        applyVariantPackToRoot(root, variantObj);
        syncVariantGallery(root, variantObj.id, payload);
      });
    } finally {
      variantSyncLock = false;
    }
  }

  function syncFrequencyToRoot(root, { sellingPlanId, planLabel, sourceInput } = {}) {
    if (!root) return null;

    let targetInput = findFrequencyInputInRoot(root, { sellingPlanId, planLabel });

    if (!targetInput && sourceInput) {
      targetInput = findFrequencyInputInRoot(root, {
        sellingPlanId: getFrequencySellingPlanId(sourceInput),
        planLabel: getFrequencyPlanLabel(sourceInput),
      });
    }

    if (!targetInput) return null;

    if (!targetInput.checked) {
      targetInput.click();
    } else {
      targetInput.dispatchEvent(new Event("change", { bubbles: true }));
    }

    const matchingCard = findPlanCardByLabel(root, planLabel || getFrequencyPlanLabel(targetInput));
    if (matchingCard) {
      updatePlanCardStates(root, matchingCard);
    }

    ensureBuyboxPackReady(root);

    const panel = Alpine.$data(root);
    if (panel?.syncFromFrequency) {
      panel.syncFromFrequency(targetInput);
    }

    return targetInput;
  }

  function syncFrequencyAcrossBuyboxes(sourceRoot, sourceInput) {
    if (frequencySyncLock || !sourceRoot || !sourceInput) return;

    const sellingPlanId = getFrequencySellingPlanId(sourceInput);
    const planLabel = getFrequencyPlanLabel(sourceInput);

    frequencySyncLock = true;
    try {
      getAllBuyboxRoots().forEach((root) => {
        if (root === sourceRoot) return;
        syncFrequencyToRoot(root, { sellingPlanId, planLabel, sourceInput });
      });
    } finally {
      frequencySyncLock = false;
    }

    resyncVariantPackAfterFrequencyChange(sourceRoot);
  }

  function handleVariantInteraction(event) {
    if (variantSyncLock) return;

    const selector = event.target.closest(
      ".ctr-midlife-trial-lp-product-buybox-compare__flavor, .ctr-midlife-trial-lp-product-buybox__variant-pack .variant-pack-form__selector, .ctr-midlife-trial-lp-product-buybox__skio-engine .variant-pack-form__selector"
    );
    const action = event.target.closest(".variant-pack-form__selector-action");

    if (!selector && !action) return;

    const sourceRoot = (selector || event.target).closest("[data-ctr-ml-trial-lp-buybox]");
    if (!sourceRoot) return;

    window.requestAnimationFrame(() => {
      window.setTimeout(() => {
        const variantObj = getSelectedVariantFromRoot(sourceRoot);
        if (!variantObj) return;

        syncVariantAcrossBuyboxes(
          sourceRoot,
          variantObj,
          findVariantSelectorData(sourceRoot, variantObj.id)?.variantChangePayload
        );
      }, 0);
    });
  }

  function scrollToBuybox() {
    const target = getBuyboxElement();
    if (!target) return Promise.resolve(false);
    const top = target.getBoundingClientRect().top + window.scrollY - HEADER_OFFSET;
    window.scrollTo({ top, behavior: "smooth" });
    return new Promise((resolve) => {
      window.setTimeout(() => resolve(true), 450);
    });
  }

  function getHeaderOffset() {
    const headerMini = document.querySelector(".header .header__mini");
    if (headerMini?.offsetHeight) return headerMini.offsetHeight;
    return headerMini?.classList.contains("is-collapsed") ? 44 : HEADER_OFFSET;
  }

  function scrollToTestimonials(sourceEl) {
    const href = sourceEl?.getAttribute?.("href");
    let targetId = DEFAULT_TESTIMONIALS_SECTION_ID;

    if (href?.startsWith("#")) {
      targetId = href.slice(1);
    }

    const target = document.getElementById(targetId);
    if (!target) return Promise.resolve(false);

    const top = target.getBoundingClientRect().top + window.scrollY - getHeaderOffset();
    window.scrollTo({ top, behavior: "smooth" });
    return new Promise((resolve) => {
      window.setTimeout(() => resolve(true), 450);
    });
  }

  let atcTriggerLock = false;

  function getSkioForm(root) {
    return (
      root?.querySelector(".ctr-midlife-trial-lp-product-buybox__variant-pack .variant-pack-form") ||
      root?.querySelector(".ctr-midlife-trial-lp-product-buybox__skio-engine .variant-pack-form")
    );
  }

  function getSkioFormData(root) {
    const form = getSkioForm(root);
    if (!form) return null;
    try {
      return window.Alpine?.$data?.(form) || null;
    } catch (_e) {
      return null;
    }
  }

  function preselectFirstVariant(root) {
    const form = getSkioForm(root);
    if (!form) return false;

    const formData = getSkioFormData(root);
    if (!formData) return false;

    if (form.dataset.ctrMlTrialLpVariantPreselected) {
      if (formData.isFull?.()) return true;
      delete form.dataset.ctrMlTrialLpVariantPreselected;
    }

    if (formData.isFull?.()) {
      form.dataset.ctrMlTrialLpVariantPreselected = "true";
      return true;
    }

    const preferredTitle = (form.dataset.preselectVariantTitle || "").trim().toLowerCase();
    const selectors = Array.from(form.querySelectorAll(".variant-pack-form__selector"));
    const preferredSelector = preferredTitle
      ? selectors.find((sel) => {
          const title = (window.Alpine.$data(sel)?.variant?.variantTitle || "").toLowerCase();
          return title === preferredTitle || title.includes(preferredTitle);
        })
      : null;
    const firstSelector = preferredSelector || selectors[0];
    const firstVariant = firstSelector && window.Alpine.$data(firstSelector)?.variant;
    if (!firstVariant) return false;

    if (formData.isInPack?.(firstVariant.id)) {
      form.dataset.ctrMlTrialLpVariantPreselected = "true";
      return formData.isFull?.() ?? true;
    }

    if (formData.isSingleSelect?.()) {
      formData.toggleSingleSelection?.(firstVariant);
    } else if (formData.addToPack) {
      formData.addToPack(firstVariant);
    } else {
      firstSelector.click();
    }

    if (formData.isFull?.()) {
      form.dataset.ctrMlTrialLpVariantPreselected = "true";
      return true;
    }

    return false;
  }

  function ensureBuyboxPackReady(root) {
    root = root || getBuyboxRoot();
    const formData = getSkioFormData(root);
    if (!formData) return false;
    if (formData.isFull?.()) return true;
    return preselectFirstVariant(root);
  }

  async function triggerBuyboxAtc() {
    if (atcTriggerLock) return true;

    const root = getBuyboxRoot();
    if (!root) {
      await scrollToBuybox();
      return false;
    }

    ensureBuyboxPackReady(root);

    const formData = getSkioFormData(root);
    if (formData?.isFull?.() && typeof formData.addToCart === "function") {
      atcTriggerLock = true;
      try {
        await performBuyboxAddToCart(formData, root);
        delete getSkioForm(root)?.dataset.ctrMlTrialLpVariantPreselected;
        preselectFirstVariant(root);
        return true;
      } catch (_e) {
        /* fall through to hidden button click */
      } finally {
        window.setTimeout(() => {
          atcTriggerLock = false;
        }, 1500);
      }
    }

    const btn = root.querySelector(
      ".ctr-midlife-trial-lp-product-buybox__skio-engine .variant-pack-form__submit-wrapper button:not([disabled])"
    );
    if (btn) {
      atcTriggerLock = true;
      btn.click();
      window.setTimeout(() => {
        atcTriggerLock = false;
        delete getSkioForm(root)?.dataset.ctrMlTrialLpVariantPreselected;
        preselectFirstVariant(root);
      }, 1500);
      return true;
    }

    return scrollToBuybox();
  }

  async function triggerBuyboxOneTimeAtc() {
    if (atcTriggerLock) return true;

    const root = getBuyboxRoot();
    if (!root) {
      await scrollToBuybox();
      return false;
    }

    ensureBuyboxPackReady(root);

    const formData = getSkioFormData(root);
    if (!formData?.isFull?.() || typeof formData.addToCart !== "function") {
      return scrollToBuybox();
    }

    const savedPlanId = formData.selectedSellingPlanID;
    atcTriggerLock = true;

    try {
      formData.setSellingPlanID(null);
      await performBuyboxAddToCart(formData, root);
      delete getSkioForm(root)?.dataset.ctrMlTrialLpVariantPreselected;
      preselectFirstVariant(root);
      return true;
    } catch (_e) {
      return false;
    } finally {
      if (savedPlanId) {
        formData.setSellingPlanID(savedPlanId);
      }
      window.setTimeout(() => {
        atcTriggerLock = false;
      }, 1500);
    }
  }

  async function handleScrollSelectAtc(planFrequency) {
    await scrollToBuybox();

    if (planFrequency) {
      selectPlanByFrequency(planFrequency);
      window.setTimeout(() => {
        const root = getBuyboxRoot();
        if (root) {
          const panel = window.Alpine?.$data?.(root);
          const checkedInput = root.querySelector(
            '.ctr-midlife-trial-lp-product-buybox__skio-engine [name="frequency"]:checked'
          );
          if (panel?.syncFromFrequency && checkedInput) {
            panel.syncFromFrequency(checkedInput);
          }
        }
      }, 150);
    }

    window.setTimeout(() => {
      triggerBuyboxAtc();
    }, planFrequency ? 400 : 200);
  }

  function onDocumentClick(event) {
    const testimonialsTrigger = event.target.closest(SCROLL_TO_TESTIMONIALS_SELECTOR);
    if (testimonialsTrigger) {
      event.preventDefault();
      scrollToTestimonials(testimonialsTrigger);
      return;
    }

    const planCard = event.target.closest(PLAN_CARD_SELECTOR);
    if (planCard) {
      event.preventDefault();
      selectPlanCard(planCard, planCard.closest("[data-ctr-ml-trial-lp-buybox]"));
      return;
    }

    const scrollTrigger = event.target.closest(SCROLL_SELECTOR);
    if (scrollTrigger) {
      event.preventDefault();
      const planFrequency = scrollTrigger.dataset.ctrMlTrialLpSelectPlan;
      const shouldAtc = scrollTrigger.hasAttribute("data-ctr-ml-trial-lp-select-atc");

      if (shouldAtc) {
        handleScrollSelectAtc(planFrequency);
      } else {
        scrollToBuybox();
        if (planFrequency) {
          window.setTimeout(() => {
            selectPlanByFrequency(planFrequency);
          }, 350);
        }
      }
      return;
    }

    const atcTrigger = event.target.closest(ATC_SELECTOR);
    if (atcTrigger) {
      event.preventDefault();
      triggerBuyboxAtc();
      return;
    }

    const oneTimeTrigger = event.target.closest(ONE_TIME_ATC_SELECTOR);
    if (oneTimeTrigger) {
      event.preventDefault();
      triggerBuyboxOneTimeAtc();
    }
  }

  function formatMoneyFromCents(cents) {
    if (!cents || cents <= 0) return "";
    const hasFraction = Math.round(cents) % 100 !== 0;
    if (typeof Shopify === "undefined" || !Shopify.formatMoney) {
      return `$${(cents / 100).toFixed(hasFraction ? 2 : 0)}`;
    }
    const baseFormat = Shopify.money_format || "${{amount}}";
    const format = hasFraction ? baseFormat : baseFormat.replace("amount", "amount_no_decimals");
    return Shopify.formatMoney(cents, format);
  }

  const PER_DAY_BASIS_DEFAULT = 30;

  function getPerDayBasis(root) {
    root = root || getBuyboxRoot();
    const pricesEl = root?.querySelector('[x-ref="productTopPrices"]');
    try {
      const data = pricesEl && window.Alpine?.$data?.(pricesEl);
      if (data?.piecePerPack > 0) return data.piecePerPack;
    } catch (_e) {
      /* Alpine may not be ready */
    }
    return PER_DAY_BASIS_DEFAULT;
  }

  function formatPerDayFromCents(cents, dayBasis) {
    const basis = dayBasis || getPerDayBasis();
    if (!cents || cents <= 0 || !basis) return "";
    const perDay = cents / 100 / basis;
    return `$${perDay.toFixed(2)}`;
  }

  function formatPromoPerDayFromCents(compareCents, dayBasis, discountPercent) {
    const basis = dayBasis || getPerDayBasis();
    const discount = discountPercent > 0 ? discountPercent : 40;
    if (!compareCents || compareCents <= 0 || !basis) return "";
    const promoPrice = compareCents * (1 - discount / 100);
    return `$${(promoPrice / 100 / basis).toFixed(2)}`;
  }

  function isValidDisplayPrice(price) {
    if (!price || typeof price !== "string") return false;
    const normalized = price.replace(/\s+/g, " ").trim();
    if (normalized === "$0" || normalized === "$0.00") return false;
    return /^\$[\d,]+(?:\.\d{2})?/.test(normalized);
  }

  function parseDisplayPriceToCents(price) {
    if (!price || typeof price !== "string") return null;
    const match = price.replace(/,/g, "").match(/\$([\d]+(?:\.\d{2})?)/);
    if (!match) return null;
    return Math.round(parseFloat(match[1]) * 100);
  }

  function readFrequencyPricing(input) {
    if (!input) return null;

    let data = null;
    try {
      data = window.Alpine?.$data?.(input);
    } catch (_e) {
      /* Alpine may not be ready */
    }

    if (data?.rawPrice != null && data.rawPrice > 0) {
      const compareCents = data.rawComparePrice || data.rawOriginalPrice;
      const discountPercent =
        compareCents > data.rawPrice
          ? Math.round((1 - data.rawPrice / compareCents) * 100)
          : data.subscriptionDiscountPercent > 0
            ? data.subscriptionDiscountPercent
            : 0;

      return {
        price: formatMoneyFromCents(data.rawPrice),
        compare: compareCents ? formatMoneyFromCents(compareCents) : null,
        rawPrice: data.rawPrice,
        rawComparePrice: compareCents,
        deliveryDays: data.deliveryDays || data.servingsCount || 0,
        discountPercent,
        isSubscription: Boolean(data.sellingPlanID),
      };
    }

    return null;
  }

  function applyPricingTemplate(template, values) {
    if (!template) return "";
    return template
      .replace(/\[discount\]/g, values.discount)
      .replace(/\[discount_percent\]/g, values.discount)
      .replace(/\[per_day\]/g, values.perDay)
      .replace(/\[days\]/g, values.days)
      .replace(/\[plan_name\]/g, values.planName);
  }

  function formatPerServingFromCents(cents, root) {
    return formatPerDayFromCents(cents, getPerDayBasis(root));
  }

  function normalizePerServingValue(value) {
    if (!value || typeof value !== "string") return "";
    return value.replace(/\/(day|serving).*/i, "").trim();
  }

  function formatDisplayBadge(discountPercent, fallback) {
    const parsed = parseInt(discountPercent, 10);
    if (parsed > 0) return `${parsed}% OFF TODAY`;
    if (!fallback) return "";

    const normalized = fallback.trim();
    if (/off/i.test(normalized) && !/today/i.test(normalized)) {
      return `${normalized} TODAY`;
    }
    return normalized;
  }

  function readPlanCardPricing(card) {
    if (!card) return null;

    let perDay = card.dataset.perDay?.trim() || "";
    if (!perDay) {
      const meta =
        card.querySelector(".ctr-midlife-trial-lp-product-buybox__plan-card-meta span:first-child")
          ?.textContent || "";
      const perDayMatch = meta.match(/\$([\d.]+)\/day/i);
      if (perDayMatch) perDay = `$${perDayMatch[1]}`;
    }

    let discountPercent = null;
    const discountRaw = card.dataset.discountPercent;
    if (discountRaw != null && discountRaw !== "") {
      discountPercent = parseInt(discountRaw, 10);
    }
    if (!Number.isFinite(discountPercent)) {
      const accent =
        card.querySelector(".ctr-midlife-trial-lp-product-buybox__plan-card-meta-accent")?.textContent ||
        "";
      const discountMatch = accent.match(/(\d+)\s*%/);
      if (discountMatch) discountPercent = parseInt(discountMatch[1], 10);
    }

    return {
      perDay,
      discountPercent: Number.isFinite(discountPercent) ? discountPercent : null,
    };
  }

  function buildTemplateValues(pricing, planName, root, activeCard, fallbackPriceCents) {
    const cardPricing = readPlanCardPricing(activeCard);

    let perDay =
      normalizePerServingValue(cardPricing?.perDay) ||
      (pricing?.rawPrice ? formatPerServingFromCents(pricing.rawPrice, root) : "");

    if (!perDay && fallbackPriceCents > 0) {
      perDay = formatPerServingFromCents(fallbackPriceCents, root);
    }

    if (!perDay && root) {
      const priceEl = root.querySelector(".ctr-midlife-trial-lp-product-buybox__price-current");
      const priceCents = parseDisplayPriceToCents(priceEl?.textContent?.trim() || "");
      if (priceCents > 0) {
        perDay = formatPerServingFromCents(priceCents, root);
      }
    }

    let discount =
      cardPricing?.discountPercent != null
        ? String(cardPricing.discountPercent)
        : pricing?.isSubscription
          ? String(pricing.discountPercent)
          : "";

    if (!discount && root) {
      const badgeEl = root.querySelector(".ctr-midlife-trial-lp-product-buybox__price-badge-text");
      const badgeMatch = badgeEl?.textContent?.match(/(\d+)\s*%/);
      if (badgeMatch) discount = badgeMatch[1];
    }

    const days = pricing?.deliveryDays ? String(pricing.deliveryDays) : "";

    return { discount, perDay, days, planName: planName || "" };
  }

  function getSelectedPlanCard(root) {
    return root?.querySelector(`${PLAN_CARD_SELECTOR}.is-selected`) || root?.querySelector(`${PLAN_CARD_SELECTOR}[aria-checked="true"]`);
  }

  function updatePlanCardStates(root, activeCard) {
    if (!root) return;
    root.querySelectorAll(PLAN_CARD_SELECTOR).forEach((card) => {
      const selected = card === activeCard;
      card.classList.toggle("is-selected", selected);
      card.setAttribute("aria-checked", selected ? "true" : "false");
    });
  }

  function selectPlanByFrequency(frequency) {
    const roots = getAllBuyboxRoots();
    if (roots.length === 0 || !frequency) return false;

    const normalized = frequency.trim().toLowerCase();
    let matched = false;

    for (const root of roots) {
      const cards = root.querySelectorAll(PLAN_CARD_SELECTOR);

      for (const card of cards) {
        const label =
          card.dataset.planLabel ||
          card.querySelector(".ctr-midlife-trial-lp-product-buybox__plan-card-label")?.textContent ||
          "";
        if (label.trim().toLowerCase() === normalized) {
          selectPlanCard(card, root);
          matched = true;
          break;
        }
      }

      if (matched) break;

      const hiddenFrequencies = root.querySelectorAll(
        '.ctr-midlife-trial-lp-product-buybox__skio-engine [name="frequency"]'
      );

      for (const input of hiddenFrequencies) {
        const inputLabel = getFrequencyPlanLabel(input);
        if (inputLabel.trim().toLowerCase().includes(normalized)) {
          frequencySyncLock = true;
          try {
            getAllBuyboxRoots().forEach((buyboxRoot) => {
              syncFrequencyToRoot(buyboxRoot, {
                sellingPlanId: getFrequencySellingPlanId(input),
                planLabel: inputLabel,
                sourceInput: input,
              });
            });
          } finally {
            frequencySyncLock = false;
          }

          matched = true;
          break;
        }
      }

      if (matched) break;
    }

    if (matched) {
      resyncVariantPackAfterFrequencyChange(getBuyboxRoot());
    }

    return matched;
  }

  function selectPlanCard(card, sourceRoot) {
    sourceRoot = sourceRoot || card?.closest("[data-ctr-ml-trial-lp-buybox]");
    if (!card) return;

    const inputId = card.dataset.frequencyInput;
    const hiddenInput = inputId ? document.getElementById(inputId) : null;
    const planLabel =
      card.dataset.planLabel ||
      card.querySelector(".ctr-midlife-trial-lp-product-buybox__plan-card-label")?.textContent?.trim() ||
      "";
    const sellingPlanId = hiddenInput ? getFrequencySellingPlanId(hiddenInput) : null;

    frequencySyncLock = true;
    try {
      getAllBuyboxRoots().forEach((root) => {
        if (root === sourceRoot && hiddenInput) {
          if (!hiddenInput.checked) {
            hiddenInput.click();
          } else {
            hiddenInput.dispatchEvent(new Event("change", { bubbles: true }));
          }

          updatePlanCardStates(root, card);
          ensureBuyboxPackReady(root);

          const panel = Alpine.$data(root);
          if (panel?.syncFromFrequency) {
            panel.syncFromFrequency(hiddenInput);
          }
          return;
        }

        syncFrequencyToRoot(root, {
          sellingPlanId,
          planLabel,
          sourceInput: hiddenInput,
        });
      });
    } finally {
      frequencySyncLock = false;
    }

    if (sourceRoot) {
      resyncVariantPackAfterFrequencyChange(sourceRoot);
    }
  }

  function selectInitialFrequency(root) {
    if (!root) return;

    const index = parseInt(root.dataset.initialFrequency || "0", 10);
    const cards = root.querySelectorAll(PLAN_CARD_SELECTOR);
    const hiddenFrequencies = root.querySelectorAll('[name="frequency"]');
    const targetCard = cards[index] || cards[0];
    const targetInput = hiddenFrequencies[index] || hiddenFrequencies[0];

    if (targetCard) {
      selectPlanCard(targetCard, root);
      return;
    }

    if (hiddenFrequencies.length === 0) {
      ensureBuyboxPackReady(root);
      return;
    }

    if (!targetInput) return;

    if (!targetInput.checked) {
      targetInput.checked = true;
    }

    targetInput.dispatchEvent(new Event("change", { bubbles: true }));

    ensureBuyboxPackReady(root);

    const panel = Alpine.$data(root);
    if (panel?.syncFromFrequency) {
      panel.syncFromFrequency(targetInput);
    }
  }

  function initBuyboxVariantPreselect(root) {
    if (!root || preselectFirstVariant(root)) return;

    let attempts = 0;
    const timer = window.setInterval(() => {
      attempts += 1;
      if (preselectFirstVariant(root) || attempts >= 20) {
        window.clearInterval(timer);
      }
    }, 250);
  }

  document.addEventListener("click", onDocumentClick);
  document.addEventListener("click", handleVariantInteraction);

  document.addEventListener("keydown", (event) => {
    if (event.key !== "Enter" && event.key !== " ") return;

    const testimonialsTrigger = event.target.closest(SCROLL_TO_TESTIMONIALS_SELECTOR);
    if (!testimonialsTrigger || testimonialsTrigger.tagName === "A") return;

    event.preventDefault();
    scrollToTestimonials(testimonialsTrigger);
  });

  document.addEventListener("change", (event) => {
    const input = event.target;
    if (!input?.matches?.('.ctr-midlife-trial-lp-product-buybox [name="frequency"]')) return;
    if (frequencySyncLock) return;

    const sourceRoot = input.closest("[data-ctr-ml-trial-lp-buybox]");
    if (!sourceRoot) return;

    syncFrequencyAcrossBuyboxes(sourceRoot, input);
  });

  window._mfrAlpineRegistered = window._mfrAlpineRegistered || {};

  function registerCtrMlTrialLpAlpineData() {
    if (window._mfrAlpineRegistered.ctrMlTrialLpBuybox) return;

    Alpine.data("ctrMlTrialLpBuyboxPanel", () => ({
      displayPrice: "",
      displayCompare: "",
      displayPriceNote: "",
      displayBadge: "",
      _fallbackPrice: "",
      _fallbackCompare: "",
      _fallbackBadge: "",
      _templates: {},

      init() {
        const root = this.$el;

        try {
          this._templates = JSON.parse(root.dataset.ctrMlTrialLpBuyboxConfig || "{}");
        } catch (_e) {
          this._templates = {};
        }

        const priceEl = root.querySelector(".ctr-midlife-trial-lp-product-buybox__price-current");
        const compareEl = root.querySelector(".ctr-midlife-trial-lp-product-buybox__price-compare");
        const badgeEl = root.querySelector(".ctr-midlife-trial-lp-product-buybox__price-badge");
        const priceNoteEl = root.querySelector("[data-ctr-ml-trial-lp-buybox-price-note]");

        this._fallbackPrice = priceEl?.textContent?.trim() || "";
        this._fallbackCompare = compareEl?.textContent?.trim() || "";
        this._fallbackBadge = badgeEl?.textContent?.trim() || "";
        this.displayPrice = this._fallbackPrice;
        this.displayCompare = this._fallbackCompare;
        this.displayBadge = this._fallbackBadge;
        this.displayPriceNote = priceNoteEl?.innerHTML?.trim() || "";

        if (!this._templates.priceNote && priceNoteEl) {
          this._templates.priceNote = priceNoteEl.dataset.template || priceNoteEl.innerHTML?.trim() || "";
        }

        this.$nextTick(() => {
          bindBuyboxGallerySection(root);
          selectInitialFrequency(root);
          initBuyboxVariantPreselect(root);

          const checkedInput = root.querySelector(
            '.ctr-midlife-trial-lp-product-buybox__skio-engine [name="frequency"]:checked'
          );
          this.syncFromFrequency(checkedInput);
        });

        root.querySelector(".ctr-midlife-trial-lp-product-buybox__skio-engine")?.addEventListener("change", (event) => {
          if (event.target?.matches?.('[name="frequency"]')) {
            if (frequencySyncLock) return;
            this.syncFromFrequency(event.target);
            ensureBuyboxPackReady(root);
          }
        });
      },

      syncFromFrequency(input) {
        const root = this.$el;
        let active =
          input ||
          root.querySelector('[name="frequency"]:checked') ||
          root.querySelector('.ctr-midlife-trial-lp-product-buybox__skio-engine [name="frequency"]:checked');

        if (!active) {
          const frequencies = root.querySelectorAll('[name="frequency"]');
          const index = parseInt(root.dataset.initialFrequency || "0", 10);
          active = frequencies[index] || frequencies[0] || null;

          if (active && !active.checked) {
            active.checked = true;
            active.dispatchEvent(new Event("change", { bubbles: true }));
          }
        }

        let pricing = readFrequencyPricing(active);

        const activeCard =
          (active &&
            root.querySelector(`${PLAN_CARD_SELECTOR}[data-frequency-input="${active.id}"]`)) ||
          getSelectedPlanCard(root);
        const cardDisplayPrice = activeCard?.dataset.displayPrice || "";

        if (!pricing?.price || !isValidDisplayPrice(pricing.price)) {
          const cardCents = parseDisplayPriceToCents(cardDisplayPrice);
          if (cardCents) {
            pricing = {
              price: formatMoneyFromCents(cardCents),
              compare: this._fallbackCompare ? this._fallbackCompare : null,
              rawPrice: cardCents,
              rawComparePrice: parseDisplayPriceToCents(this._fallbackCompare),
              deliveryDays: getPerDayBasis(root),
              discountPercent: 0,
              isSubscription: true,
            };
          }
        }

        if (pricing?.price && isValidDisplayPrice(pricing.price)) {
          this.displayPrice = pricing.price;
        } else {
          this.displayPrice = this._fallbackPrice;
        }

        if (pricing?.compare && isValidDisplayPrice(pricing.compare)) {
          this.displayCompare = pricing.compare;
        } else if (this._fallbackCompare) {
          this.displayCompare = this._fallbackCompare;
        }

        const planName =
          activeCard?.dataset.planLabel || getFrequencyPlanLabel(active) || "";
        const fallbackPriceCents =
          pricing?.rawPrice ||
          parseDisplayPriceToCents(this.displayPrice || this._fallbackPrice) ||
          0;
        const templateValues = buildTemplateValues(
          pricing,
          planName,
          root,
          activeCard,
          fallbackPriceCents
        );

        if (this._templates.priceNote) {
          this.displayPriceNote = applyPricingTemplate(this._templates.priceNote, templateValues);
        }

        this.displayBadge = formatDisplayBadge(templateValues.discount, this._fallbackBadge);

        if (active) {
          const inputId = active.id;
          const matchingCard = root.querySelector(`${PLAN_CARD_SELECTOR}[data-frequency-input="${inputId}"]`);
          if (matchingCard) {
            updatePlanCardStates(root, matchingCard);
          }
        }
      },
    }));

    window._mfrAlpineRegistered.ctrMlTrialLpBuybox = true;
  }

  document.addEventListener("alpine:init", registerCtrMlTrialLpAlpineData);
  if (window.Alpine?.data) {
    registerCtrMlTrialLpAlpineData();
  }

  const BUYBOX_RATING_SELECTOR = "[data-ctr-ml-trial-lp-buybox-rating]";

  function ensureBuyboxRating(container) {
    if (!container || container.dataset.ctrMlTrialLpRatingReady) return true;
    if (container.classList.contains("ctr-midlife-trial-lp-product-buybox__rating--static")) {
      container.dataset.ctrMlTrialLpRatingReady = "true";
      return true;
    }

    const okeSr = container.querySelector(".oke-sr");
    if (!okeSr || !okeSr.querySelector(".oke-sr-stars")) return false;

    container.dataset.ctrMlTrialLpRatingReady = "true";
    return true;
  }

  function watchBuyboxRatings(root = document) {
    root.querySelectorAll(BUYBOX_RATING_SELECTOR).forEach((container) => {
      if (ensureBuyboxRating(container)) return;

      const observer = new MutationObserver(() => {
        if (ensureBuyboxRating(container)) {
          observer.disconnect();
        }
      });
      observer.observe(container, { childList: true, subtree: true, characterData: true });
    });
  }

  function initBuyboxRatings() {
    watchBuyboxRatings();
    window.setTimeout(() => watchBuyboxRatings(), 1500);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initBuyboxRatings);
  } else {
    initBuyboxRatings();
  }

  document.addEventListener("okeWidgetInitialised", initBuyboxRatings);
  document.addEventListener("oke-reviews-widget-loaded", initBuyboxRatings);

  window.ctrMlTrialLpBuybox = {
    scrollToBuybox,
    scrollToTestimonials,
    triggerBuyboxAtc,
    triggerBuyboxOneTimeAtc,
    getBuyboxElement,
    getBuyboxRoot,
    getAllBuyboxRoots,
    selectPlanCard,
    selectPlanByFrequency,
    selectInitialFrequency,
    readFrequencyPricing,
    syncVariantAcrossBuyboxes,
    syncFrequencyAcrossBuyboxes,
  };
})();
